import { useUsersApi } from "@/Hooks/useUsersApi";
import type { Invitation } from "@freightsimple/generated-dashboard-openapi-client/models/Invitation";
import type { SendInvitationResponse } from "@freightsimple/generated-dashboard-openapi-client/models/SendInvitationResponse";
import type { SwitchToLinkedCompanyResponse } from "@freightsimple/generated-dashboard-openapi-client/models/SwitchToLinkedCompanyResponse";
import type { User } from "@freightsimple/generated-dashboard-openapi-client/models/User";

import { useAuthentication } from "@/Slices/authenticationSlice";

interface UserService {
  deleteAccount: () => Promise<void>;
  listUsersForCompany: () => Promise<User[]>;
  listInvitationsForCompany: () => Promise<Invitation[]>;
  revokeInvitation: (_: string) => Promise<void>;
  resendInvitation: (_: string) => Promise<SendInvitationResponse>;
  switchToLinkedCompany: (
    targetCompanyId: string,
  ) => Promise<SwitchToLinkedCompanyResponse>;
}

export function useUserService(): UserService {
  const api = useUsersApi();
  const { refreshAuthentication } = useAuthentication();

  async function deleteAccount() {
    await api.deleteAccount();
    alert("Account deleted");
  }

  async function listUsersForCompany() {
    return await api.listForCompany();
  }

  async function listInvitationsForCompany() {
    return await api.listInvitationsForCompany();
  }

  async function revokeInvitation(inviteeEmailAddress: string) {
    return await api.revokeInvitation({
      revokeInvitationRequest: {
        inviteeEmailAddress,
      },
    });
  }

  async function resendInvitation(inviteeEmailAddress: string) {
    return await api.resendInvitation({
      resendInvitationRequest: {
        inviteeEmailAddress,
      },
    });
  }

  async function switchToLinkedCompany(
    targetCompanyId: string,
  ): Promise<SwitchToLinkedCompanyResponse> {
    try {
      const response = await api.switchToLinkedCompany({
        switchToLinkedCompanyRequest: {
          targetCompanyId,
        },
      });

      if (response.success) {
        await refreshAuthentication();
      }

      return response;
    } catch (error) {
      console.error("Error switching to linked company:", error);
      return {
        success: false,
        errorMessage: "Network error occurred",
      };
    }
  }

  return {
    deleteAccount,
    listUsersForCompany,
    listInvitationsForCompany,
    revokeInvitation,
    resendInvitation,
    switchToLinkedCompany,
  };
}
